<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import pMap from 'p-map';

import { useLocale } from '../../composables/useLocale';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';
import { useManageGroup } from '../../composables/useManageGroup';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';

const { t } = useLocale();
const { organization } = useCurrentOrganization();
const { checkDeleteUsersInGroup } = useManageGroup();

const props = defineProps<{
  selectedGroupUids: string[];
  visible?: boolean;
}>();

const isPreparing = ref(false);
const lastPreparedTimeStamp = ref(0);

const checkDeleteUsersInGroupResponse = ref<any>(null);
const usersAllowedToBeDeleted = computed(() => {
  return checkDeleteUsersInGroupResponse.value?.allowed ?? [];
});
const hasCheckCompleted = computed(() => checkDeleteUsersInGroupResponse.value !== null);
const hasUsersToDelete = computed(() => usersAllowedToBeDeleted.value.length > 0);
const usersNotAllowedToBeDeleted = computed(() => {
  const result: Record<string, CustomerMgmtTypes.UserOutput[]> = {};
  if (!checkDeleteUsersInGroupResponse.value) {
    return result;
  }

  Object.entries(checkDeleteUsersInGroupResponse.value).forEach(([reason, users]) => {
    if (reason !== 'allowed') {
      result[reason] = users as CustomerMgmtTypes.UserOutput[];
    }
  });

  return result;
});

const checkDeleteSelectedGroupsUsers = async () => {
  const response = {} as Awaited<ReturnType<typeof checkDeleteUsersInGroup>>;
  await pMap(
    props.selectedGroupUids,
    async (groupUid) => {
      const newResponse = await checkDeleteUsersInGroup(groupUid, organization.value.uid);
      Object.entries(newResponse).forEach(([key, value]) => {
        if (!response[key]) {
          response[key] = [];
        }
        response[key] = response[key].concat(value);
      });
    },
    { concurrency: 5 },
  );

  lastPreparedTimeStamp.value = Date.now();
  return response;
};

const checkStatus = async () => {
  if (Date.now() - lastPreparedTimeStamp.value < 2 * 60 * 1000 && lastPreparedTimeStamp.value !== 0) {
    return;
  }

  isPreparing.value = true;
  try {
    const response = await checkDeleteSelectedGroupsUsers();
    checkDeleteUsersInGroupResponse.value = response;
  } catch (e) {
    console.log({ e });
    checkDeleteUsersInGroupResponse.value = null;
  }
  isPreparing.value = false;
};

const refresh = async () => {
  lastPreparedTimeStamp.value = 0;
  await checkStatus();
};

watch(
  () => props.visible,
  async (newVal) => {
    if (newVal) {
      await refresh();
    }
  },
  { immediate: true },
);

defineExpose({
  isPreparing,
  lastPreparedTimeStamp,
  usersAllowedToBeDeleted,
  usersNotAllowedToBeDeleted,
  hasCheckCompleted,
  hasUsersToDelete,
  refresh,
});
</script>

<template>
  <v-skeleton-loader v-if="visible && isPreparing" width="100%" height="48px"></v-skeleton-loader>
  <v-expansion-panels v-else-if="visible" variant="accordion" tile static :elevation="0" :loading="true">
    <v-expansion-panel v-if="usersAllowedToBeDeleted.length > 0" expand-icon="" collapse-icon="">
      <template #title>
        <p>
          {{ t('lcp.group_users_will_be_deleted_count', { count: usersAllowedToBeDeleted.length }) }}
        </p>
      </template>
    </v-expansion-panel>
    <v-expansion-panel v-if="usersNotAllowedToBeDeleted && Object.keys(usersNotAllowedToBeDeleted).length > 0">
      <template #title>
        <p>
          {{
            t('lcp.group_users_will_not_be_deleted_count', {
              count: Object.values(usersNotAllowedToBeDeleted).reduce((acc, users) => acc + users.length, 0),
            })
          }}
        </p>
      </template>
      <template #text>
        <p v-for="(users, reason) in usersNotAllowedToBeDeleted" :key="reason" class="text-caption">
          {{ users.length }} {{ t(`lcp.permissions.${reason}`) }}
        </p>
      </template>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<style scoped lang="scss"></style>
